import { createStore, applyMiddleware, compose } from 'redux'
import thunk from 'redux-thunk'
import { routerMiddleware, routerActions } from 'connected-react-router'
import { apiMiddleware } from 'redux-api-middleware'
import { createLogger } from 'redux-logger'
import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2'
import AzureBlockUpload from 'abu-redux-middleware'

import { APICallAbortion, APIErrors, APIHeaders, APIUrl, auth, socket } from './middleware'
import createRootReducer from '../reducers'
import * as appActions from '../actions/app'
import * as contentActions from '../actions/content'
import * as CPActions from '../actions/contentPush'
import * as countryActions from '../actions/countries'
import * as creditsActions from '../actions/credits'
import * as notificationActions from '../actions/notifications'
import * as statisticsActions from '../actions/statistics'
import * as storeActions from '../actions/stores'
import * as userActions from '../actions/user'
import * as actionLogsActions from '../actions/actionLogs'
import * as configActions from '../actions/config'
import { history } from '../services/Router'

const rootReducer = createRootReducer(history)

const configureStore = (initialState) => {
    // Redux Configuration
    const middleware = []
    const enhancers = []

    // Thunk Middleware
    middleware.push(thunk)

    // Logging Middleware
    const logger = createLogger({
        level: 'info',
        collapsed: true,
    })

    // Skip redux logs in console during the tests
    if (process.env.NODE_ENV !== 'test') {
        middleware.push(logger)
    }

    // Router Middleware
    const router = routerMiddleware(history)
    middleware.push(router)

    // API auth middleware
    middleware.push(auth)

    // API Server URL replacement
    middleware.push(APIUrl)

    // API headers middleware
    middleware.push(APIHeaders)

    // API Abortion middleware
    middleware.push(APICallAbortion)

    // Socket middleware
    middleware.push(socket)

    // Azure Block Upload middleware
    middleware.push(AzureBlockUpload)

    // API Middleware
    middleware.push(apiMiddleware)

    middleware.push(APIErrors)

    // Redux DevTools Configuration
    const actionCreators = {
        ...appActions,
        ...contentActions,
        ...CPActions,
        ...countryActions,
        ...creditsActions,
        ...notificationActions,
        ...statisticsActions,
        ...storeActions,
        ...userActions,
        ...routerActions,
        ...actionLogsActions,
        ...configActions,
    }
    // If Redux DevTools Extension is installed use it, otherwise use Redux compose
    /* eslint-disable no-underscore-dangle */
    const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
        ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
              // Options: http://extension.remotedev.io/docs/API/Arguments.html
              actionCreators,
          })
        : compose
    /* eslint-enable no-underscore-dangle */

    // Apply Middleware & Compose Enhancers
    enhancers.push(applyMiddleware(...middleware))
    const enhancer = composeEnhancers(...enhancers)

    // Redux persistance
    const persistConfig = {
        key: 'root',
        storage,
        stateReconciler: autoMergeLevel2, // see "Merge Process" section for details.
        blacklist: ['promos', 'credits', 'device'],
    }

    const pReducer = persistReducer(persistConfig, rootReducer)

    // Create Store
    const store = createStore(pReducer, initialState, enhancer)

    if (module.hot) {
        module.hot.accept(
            '../reducers',
            // eslint-disable-next-line global-require
            () => store.replaceReducer(require('../reducers').default)
        )
    }

    return store
}

export default { configureStore, history }
